import DOMPurify from 'dompurify'
import {DateTime} from 'luxon'
import {Message} from '@/models/message.model.js'
import {messageDirections} from '@/constants/messages.js'
import {useUuid} from '@/composables/use-uuid.js'
import {computed} from 'vue'

/**
 * Messaging composable
 */
export function useMessaging(messages) {

  //Last message
  const lastMessage = computed(() => messages.value[messages.value.length - 1])

  //Create a generic message
  const createMessage = (body, options = {}) => {
    return Message.createFrom({
      direction: options.direction || messageDirections.INCOMING,
      sentAt: options.timestamp || DateTime.now(),
      isReply: options.isReply || false,
      body: DOMPurify.sanitize(body),
      externalId: options.externalId || null,
      error: null,
    })
  }

  //Create an inbound message (= message sent by user)
  const createIncomingMessage = (text, options = {}) => {

    //Create a new external ID for user sent messages, so that we can correlate
    //them with messages received from the query messages endpoint
    const {uuid: externalId} = useUuid()

    //Create the message
    return createMessage(text, {
      ...options,
      direction: messageDirections.INCOMING,
      externalId: externalId.value,
    })
  }

  //Create an outbound message (= message sent by Arist)
  const createOutgoingMessage = (text, options = {}) => {
    return createMessage(text, {
      ...options,
      direction: messageDirections.OUTGOING,
    })
  }

  //Append message to array
  const appendMessage = (message, options) => {
    message.setHelperFlags(lastMessage.value)
    if (options.delay) {
      setTimeout(() => {
        messages.value.push(message)
      }, options.delay)
    }
    else {
      messages.value.push(message)
    }
  }

  //Create and append an inbound message (= message sent by user)
  const appendIncomingMessage = (body, options = {}) => {
    const message = createIncomingMessage(body, options)
    appendMessage(message, options)
    return message
  }

  //Create and append an outbound message (= message sent by Arist)
  const appendOutgoingMessage = (body, options = {}) => {
    const message = createOutgoingMessage(body, options)
    appendMessage(message, options)
    return message
  }

  //Expose interface
  return {
    lastMessage,
    createMessage,
    createIncomingMessage,
    createOutgoingMessage,
    appendIncomingMessage,
    appendOutgoingMessage,
  }
}
